import {
  BIG_JOKER, MAX_PIP, MIN_PIP, RANK_A, SMALL_JOKER,
  isWildcard, rankLabel, rankOf, singleOrder, suitOf,
  type Card, type Rank,
} from "./cards.js";
import type { Ruleset } from "./ruleset.js";

export const ComboType = {
  Single: "single",
  Pair: "pair",
  Triple: "triple",
  FullHouse: "fullHouse",
  Straight: "straight",
  /** 木板 / 三连对 — three consecutive pairs. */
  ConsecutivePairs: "consecutivePairs",
  /** 钢板 — two consecutive triples. */
  ConsecutiveTriples: "consecutiveTriples",
  StraightFlush: "straightFlush",
  Bomb: "bomb",
  JokerBomb: "jokerBomb",
} as const;
export type ComboType = (typeof ComboType)[keyof typeof ComboType];

/**
 * One legal reading of a set of cards.
 *
 * Because wildcards can stand in for other cards, the same physical cards often
 * have several readings at once (♥2♥2 with level 2 is both a pair of level cards
 * and, used as wilds, a pair of anything). `cards` is therefore always the cards
 * actually played; `rank`/`power` describe how this particular reading compares.
 */
export interface Combo {
  readonly type: ComboType;
  readonly cards: readonly Card[];
  /** Comparison key within a type. Only meaningful against the same type. */
  readonly rank: number;
  readonly length: number;
  /** Position on the bomb ladder. 0 means "not a bomb". */
  readonly power: number;
}

const BOMB_POWER: Record<number, number> = {
  4: 40, 5: 50, 6: 60, 7: 70, 8: 80, 9: 90, 10: 100,
};
const JOKER_BOMB_POWER = 1000;

function straightFlushPower(rules: Ruleset): number {
  return rules.straightFlushRank === "aboveFiveBomb" ? 55 : 65;
}

export function isBomb(combo: Combo): boolean {
  return combo.power > 0;
}

/**
 * Does `a` beat `b`? A null `b` means the trick is open, so any legal combo plays.
 * Bombs beat everything that is not a bomb; otherwise the shape must match.
 */
export function beats(a: Combo, b: Combo | null): boolean {
  if (b === null) return true;
  const aBomb = a.power > 0;
  const bBomb = b.power > 0;
  if (aBomb !== bBomb) return aBomb;
  if (aBomb) {
    if (a.power !== b.power) return a.power > b.power;
    return a.rank > b.rank;
  }
  if (a.type !== b.type || a.length !== b.length) return false;
  return a.rank > b.rank;
}

// --- run helpers -------------------------------------------------------------

/**
 * Candidate consecutive rank sequences of a given length. The ace-low sequence
 * is listed last and sorts below every other run because its final element is
 * the lowest possible top card.
 */
function runsOfLength(len: number, aceLow: boolean): number[][] {
  const out: number[][] = [];
  for (let start = MIN_PIP; start + len - 1 <= MAX_PIP; start++) {
    const run: number[] = [];
    for (let i = 0; i < len; i++) run.push(start + i);
    out.push(run);
  }
  if (aceLow && len >= 2) {
    const run: number[] = [RANK_A];
    for (let i = 0; i < len - 1; i++) run.push(MIN_PIP + i);
    out.push(run);
  }
  return out;
}

const RUN_CACHE = new Map<string, number[][]>();
function runs(len: number, aceLow: boolean): number[][] {
  const key = `${len}:${aceLow}`;
  let cached = RUN_CACHE.get(key);
  if (cached === undefined) {
    cached = runsOfLength(len, aceLow);
    RUN_CACHE.set(key, cached);
  }
  return cached;
}

/**
 * The comparison rank of a run is its last element: normal runs end on their top
 * card, and the ace-low run [A,2,3,4,5] ends on 5, which is exactly where it
 * belongs — below the [2,3,4,5,6] run that tops out at 6.
 */
function runRank(run: number[]): number {
  return run[run.length - 1] ?? 0;
}

/** Can `natRanks` plus `wilds` wildcards fill `copies` of every rank in `run`? */
function fillsRun(natRanks: number[], run: number[], copies: number, wilds: number): boolean {
  const need = new Map<number, number>();
  for (const r of run) need.set(r, copies);
  for (const r of natRanks) {
    const left = need.get(r);
    if (left === undefined || left === 0) return false;
    need.set(r, left - 1);
  }
  let missing = 0;
  for (const left of need.values()) missing += left;
  return missing === wilds;
}

function rankCounts(nat: readonly Card[]): Map<number, number> {
  const counts = new Map<number, number>();
  for (const c of nat) {
    const r = rankOf(c);
    counts.set(r, (counts.get(r) ?? 0) + 1);
  }
  return counts;
}

function allPips(nat: readonly Card[]): boolean {
  for (const c of nat) if (rankOf(c) > MAX_PIP) return false;
  return true;
}

// --- classification ----------------------------------------------------------

/**
 * Every legal reading of `cards`.
 *
 * Wildcards are handled by trying each split of "used as a wild" versus "played
 * as an ordinary heart of the level rank" — at most three splits, since there
 * are only two wildcards in the deck — and then testing each combo shape against
 * the resulting natural cards. Shapes whose rank is defined by repetition (pair,
 * triple, bomb) require at least one natural card to fix the rank; a hand of
 * nothing but wildcards is already covered by the "played as level cards" split.
 */
export function classifyAll(
  cards: readonly Card[],
  level: Rank,
  rules: Ruleset,
): Combo[] {
  const n = cards.length;
  if (n === 0 || n > 10) return [];

  const wilds: Card[] = [];
  const fixed: Card[] = [];
  for (const c of cards) {
    if (isWildcard(c, level)) wilds.push(c);
    else fixed.push(c);
  }

  const out: Combo[] = [];
  const seen = new Set<string>();
  const push = (type: ComboType, rank: number, power: number) => {
    const key = `${type}:${rank}:${n}:${power}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ type, cards, rank, length: n, power });
  };

  for (let k = 0; k <= wilds.length; k++) {
    // k wildcards act as wild; the remaining ones stay ordinary level cards.
    const nat = k === wilds.length ? fixed : fixed.concat(wilds.slice(k));
    classifyWithWilds(nat, k, n, level, rules, push);
  }

  return out;
}

function classifyWithWilds(
  nat: readonly Card[],
  wilds: number,
  n: number,
  level: Rank,
  rules: Ruleset,
  push: (type: ComboType, rank: number, power: number) => void,
): void {
  const counts = rankCounts(nat);
  const pipsOnly = allPips(nat);

  // Single — a lone wildcard is just a level card, never a declared rank.
  if (n === 1 && wilds === 0) {
    const only = nat[0];
    if (only !== undefined) push(ComboType.Single, singleOrder(rankOf(only), level), 0);
  }

  // Same-rank shapes: pair, triple, n-of-a-kind bomb. All need one natural card
  // to pin the rank, and wildcards can never stand in for a joker.
  if (counts.size === 1 && nat.length > 0) {
    const entry = [...counts.entries()][0];
    if (entry !== undefined) {
      const [rank] = entry;
      const isJokerRank = rank >= SMALL_JOKER;
      if (!isJokerRank || wilds === 0) {
        if (n === 2) push(ComboType.Pair, singleOrder(rank, level), 0);
        else if (n === 3) push(ComboType.Triple, singleOrder(rank, level), 0);
        else if (n >= 4 && !isJokerRank) {
          const power = BOMB_POWER[n];
          if (power !== undefined) {
            const key = rules.levelElevatesInBombs ? singleOrder(rank, level) : rank;
            push(ComboType.Bomb, key, power);
          }
        }
      }
    }
  }

  // 四大天王 — both small and both big jokers.
  if (n === 4 && wilds === 0) {
    const small = counts.get(SMALL_JOKER) ?? 0;
    const big = counts.get(BIG_JOKER) ?? 0;
    if (small === 2 && big === 2) push(ComboType.JokerBomb, BIG_JOKER, JOKER_BOMB_POWER);
  }

  // 三带二 — a triple plus a pair, compared on the triple.
  if (n === 5 && counts.size >= 1 && counts.size <= 2) {
    const groups = [...counts.entries()];
    for (const [tripleRank, tripleHave] of groups) {
      if (tripleRank >= SMALL_JOKER) continue; // a joker triple cannot exist
      if (tripleHave > 3) continue;
      const tripleWilds = 3 - tripleHave;
      const other = groups.find(([r]) => r !== tripleRank);
      if (other === undefined) {
        // The pair comes entirely from wildcards; its rank never affects the
        // comparison, so any rank distinct from the triple will do.
        if (tripleWilds + 2 !== wilds) continue;
        push(ComboType.FullHouse, singleOrder(tripleRank, level), 0);
        continue;
      }
      const [pairRank, pairHave] = other;
      if (pairHave > 2) continue;
      if (pairRank >= SMALL_JOKER) {
        if (!rules.jokersInFullHouse || pairHave !== 2) continue;
      }
      if (tripleWilds + (2 - pairHave) !== wilds) continue;
      push(ComboType.FullHouse, singleOrder(tripleRank, level), 0);
    }
  }

  if (!pipsOnly) return; // every remaining shape is a run, and runs exclude jokers

  const natRanks = nat.map(rankOf);

  // 顺子 — exactly five consecutive cards, and 同花顺 when they share a suit.
  if (n === 5) {
    const suits = new Set(nat.map(suitOf));
    const flush = suits.size <= 1;
    for (const run of runs(5, rules.aceLowRuns)) {
      if (!fillsRun(natRanks, run, 1, wilds)) continue;
      push(ComboType.Straight, runRank(run), 0);
      if (flush) push(ComboType.StraightFlush, runRank(run), straightFlushPower(rules));
    }
  }

  // 木板 — three consecutive pairs.
  if (n === 6) {
    for (const run of runs(3, rules.aceLowRuns)) {
      if (fillsRun(natRanks, run, 2, wilds)) push(ComboType.ConsecutivePairs, runRank(run), 0);
    }
    // 钢板 — two consecutive triples.
    for (const run of runs(2, rules.aceLowRuns)) {
      if (fillsRun(natRanks, run, 3, wilds)) push(ComboType.ConsecutiveTriples, runRank(run), 0);
    }
  }
}

/** Ordering used only to pick a default reading when several are legal. */
function readingScore(c: Combo): number {
  return c.power * 1000 + c.rank;
}

/** The strongest reading of `cards`, or null if they form no legal combo. */
export function classify(cards: readonly Card[], level: Rank, rules: Ruleset): Combo | null {
  const all = classifyAll(cards, level, rules);
  let best: Combo | null = null;
  for (const c of all) {
    if (best === null || readingScore(c) > readingScore(best)) best = c;
  }
  return best;
}

/**
 * The reading of `cards` that beats `current`, or null if none does.
 *
 * This is the function the server validates plays with: a play is legal exactly
 * when *some* legal reading of the chosen cards beats the trick, which is what
 * a player at a physical table would expect.
 */
export function canBeat(
  cards: readonly Card[],
  current: Combo | null,
  level: Rank,
  rules: Ruleset,
): Combo | null {
  let best: Combo | null = null;
  for (const c of classifyAll(cards, level, rules)) {
    if (!beats(c, current)) continue;
    if (best === null || readingScore(c) > readingScore(best)) best = c;
  }
  return best;
}

const TYPE_LABELS: Record<ComboType, string> = {
  [ComboType.Single]: "single",
  [ComboType.Pair]: "pair",
  [ComboType.Triple]: "triple",
  [ComboType.FullHouse]: "full house",
  [ComboType.Straight]: "straight",
  [ComboType.ConsecutivePairs]: "three consecutive pairs",
  [ComboType.ConsecutiveTriples]: "two consecutive triples",
  [ComboType.StraightFlush]: "straight flush",
  [ComboType.Bomb]: "bomb",
  [ComboType.JokerBomb]: "four kings",
};

export function comboTypeLabel(type: ComboType): string {
  return TYPE_LABELS[type];
}

/**
 * Name a comparison rank. Ranks arrive here already promoted by `singleOrder`,
 * so 20 is the level card and 25/26 are the jokers.
 */
function rankName(rank: number): string {
  if (rank === 26) return "big joker";
  if (rank === 25) return "small joker";
  if (rank === 20) return "level card";
  return rankLabel(rank);
}

/** Human-readable description, used by the CLI and the client's rules coach. */
export function comboLabel(combo: Combo): string {
  const name = rankName(combo.rank);
  switch (combo.type) {
    case ComboType.JokerBomb:
      return "four kings";
    case ComboType.Bomb:
      return `${combo.length}-card bomb of ${name}s`;
    case ComboType.Single:
      return `single ${name}`;
    case ComboType.Pair:
      return `pair of ${name}s`;
    case ComboType.Triple:
      return `three ${name}s`;
    case ComboType.FullHouse:
      return `full house of ${name}s`;
    case ComboType.Straight:
    case ComboType.StraightFlush:
    case ComboType.ConsecutivePairs:
    case ComboType.ConsecutiveTriples:
      return `${TYPE_LABELS[combo.type]} to ${name}`;
  }
}
